import {assert} from 'Asserts';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {
  BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY,
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  FragmentProductionMethod,
  isBulkAssemblyFragmentBin,
  MethodData,
  NormalizedFragment,
  BulkAssemblyTopology,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  BAIdValue,
  BARawTextValue,
  isBAId,
  isBANumericId,
  isBARawText,
} from 'molbio/bulk-assembly/BulkAssemblyValues';

export class AssemblySelector {
  constructor(readonly state: BulkAssemblyProcedure) {}

  getTopology(): BulkAssemblyTopology {
    return this.state.topology;
  }

  // TODO: Remove. We should pick one of the enums to rely on.
  getSequenceTopology(): SequenceTopology {
    return BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY[this.getTopology()];
  }

  getMethod(): MethodData {
    return this.state.method;
  }

  isConcatenation(): boolean {
    return this.getMethod().type === BulkAssemblyMethod.CONCATENATION;
  }

  getFragmentBins(): Array<BulkAssemblyFragmentBin> {
    return this.state.binCidOrdering
      .map((binCid) => this.state.binsByCid[binCid])
      .filter((bin): bin is BulkAssemblyFragmentBin => isBulkAssemblyFragmentBin(bin));
  }

  getNumFragmentBins(): number {
    return this.getFragmentBins().length;
  }

  /**
   * Get the index of the fragment as it will be used in the construct.
   *
   * @param fragment The fragment for which we should find the index.
   */
  getConstructFragmentIndex(fragment: NormalizedFragment): number | null {
    const currentBin = fragment.bin;
    if (!isBAId(currentBin)) {
      return null;
    }
    return this.getFragmentBins().findIndex((bin) => bin.cid === currentBin.id);
  }

  getSupportedPolymerConstructType(): PolymerSubtype {
    const methodData = this.getMethod();
    if (methodData.type === BulkAssemblyMethod.CONCATENATION) {
      return methodData.parameters.polymerSubtype;
    }
    return PolymerSubtype.dna_sequence;
  }

  /**
   * Derives the fragment production method for a bin in an assembly, respecting any bin-level overrides.
   * Any ID value given for binValue must correspond to a Fragment bin, not a spacer which do
   * not have production methods associated.
   */
  getFragmentProductionMethodForBin(binValue: BAIdValue | BARawTextValue): FragmentProductionMethod {
    const {method} = this.state;
    if (isBARawText(binValue)) {
      return method.parameters.defaultFragmentProductionMethod;
    } else {
      const fragmentBin = this.state.binsByCid[binValue.id];
      assert(isBulkAssemblyFragmentBin(fragmentBin), 'Expected bin to be a fragment bin');
      return fragmentBin.fragmentProductionMethod;
    }
  }

  getRestrictionEnzymeIdsForFragment(fragment: NormalizedFragment): {
    restrictionEnzymeId5: number | null;
    restrictionEnzymeId3: number | null;
  } {
    return {
      restrictionEnzymeId5: isBANumericId(fragment.restrictionEnzyme5)
        ? fragment.restrictionEnzyme5.id
        : null,
      restrictionEnzymeId3: isBANumericId(fragment.restrictionEnzyme3)
        ? fragment.restrictionEnzyme3.id
        : null,
    };
  }

  getBinForFragment(fragment: NormalizedFragment): BulkAssemblyFragmentBin | null {
    if (isBARawText(fragment.bin)) {
      return null;
    }
    return this.state.binsByCid[fragment.bin.id] as BulkAssemblyFragmentBin;
  }
}
